import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import {
  Wine, CalendarDays, Clock, Users, Loader2, CheckCircle2, XCircle, ArrowLeft, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TIME_SLOTS, isOpenDay, toLocalDateStr, fmtDate } from "@/lib/booking";

export default function ManageBooking() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState("view"); // view | reschedule
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const load = async () => {
    try {
      const res = await api.get(`/bookings/manage/${token}`);
      setBooking(res.data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const handleCancel = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/bookings/manage/${token}/cancel`);
      setBooking(res.data);
      toast.success("Your booking has been cancelled.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not cancel.");
    } finally {
      setBusy(false);
    }
  };

  const handleReschedule = async () => {
    if (!date || !slot) return;
    setBusy(true);
    try {
      const res = await api.put(`/bookings/manage/${token}/reschedule`, {
        date: toLocalDateStr(date),
        time_slot: slot,
      });
      setBooking(res.data);
      setMode("view");
      setDate(null);
      setSlot(null);
      toast.success("Your booking has been updated.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reschedule.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="h-8 w-8 animate-spin text-[#4A5D23]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-6" data-testid="manage-not-found">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-[#722F37] mx-auto" />
          <h1 className="mt-4 font-serif text-3xl text-[#1C1917]">Booking not found</h1>
          <p className="mt-2 text-[#57534E]">This management link is invalid or has expired.</p>
          <Link to="/" className="mt-6 inline-block px-6 py-3 rounded-full bg-[#4A5D23] text-white font-semibold">
            Back to site
          </Link>
        </div>
      </div>
    );
  }

  const cancelled = booking.status === "cancelled";

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-16 px-6" data-testid="manage-booking-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#1C1917] mb-6" data-testid="manage-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-[#E7E5E4] p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-full bg-[#4A5D23]/10 flex items-center justify-center">
              <Wine className="h-6 w-6 text-[#4A5D23]" />
            </div>
            <div>
              <h1 className="font-serif text-2xl text-[#1C1917]">Manage your booking</h1>
              <p className="text-sm text-[#57534E]">Gledswood Cellar Door</p>
            </div>
          </div>

          {cancelled ? (
            <div className="rounded-xl bg-[#722F37]/8 border border-[#722F37]/20 px-4 py-3 mb-6 flex items-center gap-2 text-[#722F37] font-medium" data-testid="cancelled-badge">
              <XCircle className="h-5 w-5" /> This booking is cancelled.
            </div>
          ) : (
            <div className="rounded-xl bg-[#4A5D23]/8 border border-[#4A5D23]/20 px-4 py-3 mb-6 flex items-center gap-2 text-[#4A5D23] font-medium">
              <CheckCircle2 className="h-5 w-5" /> Confirmed
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
              <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Date</p>
              <p className="mt-1 font-medium text-[#1C1917]" data-testid="manage-date">{fmtDate(booking.date)}</p>
            </div>
            <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
              <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Time</p>
              <p className="mt-1 font-medium text-[#1C1917]" data-testid="manage-time">{booking.time_slot}</p>
            </div>
            <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
              <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Guests</p>
              <p className="mt-1 font-medium text-[#1C1917]">{booking.guests}</p>
            </div>
          </div>

          <p className="mt-4 text-sm text-[#57534E]">Booked under {booking.name} · {booking.email}</p>

          {!cancelled && mode === "view" && (
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setMode("reschedule")}
                data-testid="start-reschedule-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#4A5D23] text-white font-semibold hover:bg-[#3D4D1D] transition-all"
              >
                <RotateCcw className="h-4 w-4" /> Reschedule
              </button>
              <button
                onClick={handleCancel}
                disabled={busy}
                data-testid="cancel-booking-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#722F37] text-[#722F37] font-semibold hover:bg-[#722F37] hover:text-white transition-all disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Cancel booking
              </button>
            </div>
          )}

          {cancelled && (
            <button
              onClick={() => setMode("reschedule")}
              data-testid="rebook-btn"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#4A5D23] text-white font-semibold hover:bg-[#3D4D1D] transition-all"
            >
              <RotateCcw className="h-4 w-4" /> Pick a new date & time
            </button>
          )}

          {mode === "reschedule" && (
            <div className="mt-8 border-t border-[#E7E5E4] pt-6 animate-fade-up" data-testid="reschedule-panel">
              <div className="flex items-center gap-2 mb-4 text-[#1C1917] font-medium">
                <CalendarDays className="h-5 w-5 text-[#4A5D23]" /> Choose a new date
              </div>
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => { setDate(d); setSlot(null); }}
                disabled={(d) => d < today || !isOpenDay(d)}
                data-testid="reschedule-calendar"
                className="rounded-xl"
              />
              {date && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3 text-[#1C1917] font-medium">
                    <Clock className="h-5 w-5 text-[#4A5D23]" /> Choose a time
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {TIME_SLOTS.map((t) => (
                      <button
                        key={t}
                        data-testid={`reschedule-slot-${t.replace(/[:\s]/g, "").toLowerCase()}`}
                        onClick={() => setSlot(t)}
                        className={`py-3 px-2 rounded-xl border text-sm font-medium transition-all ${
                          slot === t
                            ? "bg-[#4A5D23] text-white border-[#4A5D23]"
                            : "border-[#E7E5E4] text-[#57534E] hover:border-[#4A5D23] hover:text-[#4A5D23]"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-6 flex gap-3">
                <button
                  onClick={handleReschedule}
                  disabled={!date || !slot || busy}
                  data-testid="confirm-reschedule-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#4A5D23] text-white font-semibold hover:bg-[#3D4D1D] transition-all disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm new time"}
                </button>
                <button
                  onClick={() => { setMode("view"); setDate(null); setSlot(null); }}
                  data-testid="cancel-reschedule-btn"
                  className="px-6 py-3 rounded-full border border-[#E7E5E4] text-[#57534E] font-semibold hover:bg-[#FDFBF7] transition-all"
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
